import React from 'react';
import { CSVLink } from 'react-csv';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import AbstractChart from './AbstractChart';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import * as actions from './redux/actions';
import { DEFAULT_BUBBLE_COLOUR } from './colorCodes';
import moment from 'moment';
import { Button } from '../UI-components/UI-Component'
import {
    CHART_BACKGROUND_COLOUR,
    FILTER_DATE_RANGE_FORMAT,
    SUB_TOPIC_CLUSTERS_CHART_TITLE_STYLE,
    DEFAULT_NO_DATA_OPTIONS,
    DEFAULT_ZOOM_TYPE,
    DEFAULT_PAN_KEY,
    SERIES_KEY,
    TITLE_KEY,
    TEXT_KEY
} from './constants/HighchartsConstants';
import {
    BUBBLE_CHART_TYPE,
    BOTTOM_MARGIN,
    LEFT_MARGIN,
    RIGHT_MARGIN,
    TOP_MARGIN,
    PLOT_BORDER_WIDTH,
    TOOLTIP_FONT_SIZE,
    X_KEY,
    Y_KEY,
    DISTRIBUTION_KEY,
    SUB_TOPIC_TOTAL_COMMENT_COUNT_KEY,
    SUB_TOPIC_TITLE_KEY,
    SUB_TOPIC_DESCRIPTION_KEY,
    SUB_TOPICS_KEY,
    TOP_5_KEYWORDS_KEY,
    COUNT_KEY,
    SUB_TOPIC_COMMENT_PERCENTAGE_KEY,
    EMOTION_COMMENT_COUNTS_KEY,
    EMOTION_PERCENTAGE_COUNTS_KEY,
    TOP_5_KEYWORDS_FORMATTED_FOR_TOOLTIP_KEY,
    SUB_TOPIC_NAME_TABLE_WIDTH,
    MAXIMUM_NUMBER_OF_TOPICS_DISPLAYED,
    COMMENT_COUNT_TABLE_WIDTH,
    EMOTION_PERCENTAGE_TABLE_WIDTH,
    SUB_TOPIC_DESCRIPTION_TABLE_WIDTH,
    BUBBLE_CHART_HEIGHT,
    BUBBLE_CHART_WIDTH
} from './constants/bubbleChartConstants';
import { calculateCategoryPercentageRoundedToOneDecimalPlace } from './CommonChartMethods'
import { ANGER, FEAR, JOY, NO_SPECIFIC_EMOTION, SADNESS } from '../../home/redux/constants';
import { t } from 'i18next';
import {
  BUBBLE_CHART_I18N_PREFIX as MY_I18N_PREFIX,
  BUBBLE_CHART_I18N_KEYS as MY_I18N_KEYS
} from './constants/i18n/BubbleCharti18nConstants';

const X_KEY_FOR_CSV                             = 'x_distribution';
const Y_KEY_FOR_CSV                             = 'y_distribution';
const SUB_TOPIC_TITLE_KEY_FOR_CSV               = 'sub_topic_title';
const SUB_TOPIC_DESCRIPTION_KEY_FOR_CSV         = 'sub_topic_description';
const SUB_TOPIC_TOTAL_COMMENT_COUNT_KEY_FOR_CSV = 'sub_topic_total_comment_count';
const TOP_5_KEYWORDS_KEY_FOR_CSV                = 'top_5_keywords';
const ANGER_COUNT_KEY_FOR_CSV                   = 'anger_count';
const FEAR_COUNT_KEY_FOR_CSV                    = 'fear_count';
const JOY_COUNT_KEY_FOR_CSV                     = 'joy_count';
const SADNESS_COUNT_KEY_FOR_CSV                 = 'sadness_count';
const NO_SPECIFIC_EMOTION_KEY_FOR_CSV           = 'no_specific_emotion_count';


class BubbleChart extends AbstractChart {
  constructor(props) {
    super(props);
    this.state = {
      selectedDataIndex: null,
      chartData: [],
      subTopicTableData: [],
      fileName: "",
      topicId: [],
      mapDataFromProps: [],
      source: [],
      filterDateRange: {
        fromDate: moment().format(FILTER_DATE_RANGE_FORMAT),
        toDate: moment().format(FILTER_DATE_RANGE_FORMAT),
      },
      mapOptions: {
        chart: {
          type: BUBBLE_CHART_TYPE,
          backgroundColor: CHART_BACKGROUND_COLOUR,
          marginBottom: BOTTOM_MARGIN,
          marginLeft: LEFT_MARGIN,
          marginRight: RIGHT_MARGIN,
          marginTop: TOP_MARGIN,
          width: BUBBLE_CHART_WIDTH,
          height: BUBBLE_CHART_HEIGHT,
          plotBorderWidth: PLOT_BORDER_WIDTH,
          zoomType: DEFAULT_ZOOM_TYPE,
          panKey: DEFAULT_PAN_KEY,
          panning: true
        },
        noData: {...DEFAULT_NO_DATA_OPTIONS},
        legend: {
            enabled: false,
        },
        title: {...SUB_TOPIC_CLUSTERS_CHART_TITLE_STYLE},
        tooltip: {
          headerFormat: null,
          pointFormat: '<span><b>{point.subTopicTitle}</b></span><br>' +
          '<span>Comments: {point.z} ({point.subTopicCommentPercentage}%)</span><br>' +
          '<span>Keywords: {point.top5KeywordsForTooltip}</span>',
          footerFormat: null,
          style: {
            fontSize: TOOLTIP_FONT_SIZE
          }
        },
        credits: {
          enabled: false,
        },
        xAxis: {
            visible: false
        },
        yAxis: {
            visible: false
        },
        plotOptions: {
          series: {
            point: {
              events: {
                click: (event) => {
                  this.highlightClickedDataRow(event);
                }
              }
            }
          }
        }
      },
    };
    this.tableContainerRef = React.createRef();
    this.csvLinkEl = React.createRef();
  }

  reRenderChartOnChange(props) {
    const { retrieveSelectedChartData } = this.props.actions;
    let { mapOptions } = this.state;
    this.setState(
      {
        mapOptions,
        source: props.source,
        filterDateRange: props.dateRangeFilterValue,
        topicId: props.selectedTopicId
      },
      () => {
        retrieveSelectedChartData({
          chartId: this.props.chartId.replace('chart', ''),
          source: this.state.source,
          topicId: this.state.topicId,
          dateFilterValue: this.state.filterDateRange
        });
      },
    );
  };

  parseDataToRequiredChartDataFormat(chartData) {
    const parsedData = [];
    const totalCount = chartData[COUNT_KEY];
    const sortedChartData = [...chartData[SUB_TOPICS_KEY]];
    sortedChartData.sort((a, b) => b[SUB_TOPIC_TOTAL_COMMENT_COUNT_KEY] - a[SUB_TOPIC_TOTAL_COMMENT_COUNT_KEY]);
    let maxNumberOfTopicsDispayed = 0; 
    if (sortedChartData.length <  MAXIMUM_NUMBER_OF_TOPICS_DISPLAYED) {
      maxNumberOfTopicsDispayed = sortedChartData.length;
    } else {
      maxNumberOfTopicsDispayed = MAXIMUM_NUMBER_OF_TOPICS_DISPLAYED;
    };
    for (let i = 0; i < maxNumberOfTopicsDispayed; i++) {
      let subTopicData = sortedChartData[i]
      const subTopicTotalCommentCount = subTopicData[SUB_TOPIC_TOTAL_COMMENT_COUNT_KEY];
      const subTopicCommentsPercentage = calculateCategoryPercentageRoundedToOneDecimalPlace(subTopicTotalCommentCount, totalCount)
      const parsedSubTopicData = {
          x: subTopicData[DISTRIBUTION_KEY][X_KEY],
          y: subTopicData[DISTRIBUTION_KEY][Y_KEY],
          z: subTopicTotalCommentCount,
          [SUB_TOPIC_TITLE_KEY]: subTopicData[SUB_TOPIC_TITLE_KEY],
          [SUB_TOPIC_DESCRIPTION_KEY]: subTopicData[SUB_TOPIC_DESCRIPTION_KEY],
          [SUB_TOPIC_TOTAL_COMMENT_COUNT_KEY]: subTopicTotalCommentCount,
          [SUB_TOPIC_COMMENT_PERCENTAGE_KEY]: subTopicCommentsPercentage,
          [TOP_5_KEYWORDS_KEY]: subTopicData[TOP_5_KEYWORDS_KEY],
          [TOP_5_KEYWORDS_FORMATTED_FOR_TOOLTIP_KEY]: subTopicData[TOP_5_KEYWORDS_KEY] ? subTopicData[TOP_5_KEYWORDS_KEY].join(', ') : '',
          [EMOTION_PERCENTAGE_COUNTS_KEY]: {
            [ANGER]: calculateCategoryPercentageRoundedToOneDecimalPlace(subTopicData[EMOTION_COMMENT_COUNTS_KEY][ANGER], subTopicTotalCommentCount),
            [FEAR]: calculateCategoryPercentageRoundedToOneDecimalPlace(subTopicData[EMOTION_COMMENT_COUNTS_KEY][FEAR], subTopicTotalCommentCount),
            [JOY]: calculateCategoryPercentageRoundedToOneDecimalPlace(subTopicData[EMOTION_COMMENT_COUNTS_KEY][JOY], subTopicTotalCommentCount),
            [SADNESS]: calculateCategoryPercentageRoundedToOneDecimalPlace(subTopicData[EMOTION_COMMENT_COUNTS_KEY][SADNESS], subTopicTotalCommentCount),
            [NO_SPECIFIC_EMOTION]: calculateCategoryPercentageRoundedToOneDecimalPlace(subTopicData[EMOTION_COMMENT_COUNTS_KEY][NO_SPECIFIC_EMOTION], subTopicTotalCommentCount)
          }
      };
      parsedData.push(parsedSubTopicData);
    }
    return parsedData;
  }

  parseDataToRequiredCsvDownloadFormat(chartData) {
    const parsedData = [];
    const sortedChartData = [...chartData[SUB_TOPICS_KEY]];
    sortedChartData.sort((a, b) => b[SUB_TOPIC_TOTAL_COMMENT_COUNT_KEY] - a[SUB_TOPIC_TOTAL_COMMENT_COUNT_KEY]);
    let maxNumberOfTopicsDispayed = 0; 
    if (sortedChartData.length <  MAXIMUM_NUMBER_OF_TOPICS_DISPLAYED) {
      maxNumberOfTopicsDispayed = sortedChartData.length;
    } else {
      maxNumberOfTopicsDispayed = MAXIMUM_NUMBER_OF_TOPICS_DISPLAYED;
    };
    for (let i = 0; i < maxNumberOfTopicsDispayed; i++) {
      let subTopicData = sortedChartData[i];
      const subTopicTotalCommentCount = subTopicData[SUB_TOPIC_TOTAL_COMMENT_COUNT_KEY];
      const parsedSubTopicData = {
          [SUB_TOPIC_TITLE_KEY_FOR_CSV]: subTopicData[SUB_TOPIC_TITLE_KEY],
          [SUB_TOPIC_DESCRIPTION_KEY_FOR_CSV]: subTopicData[SUB_TOPIC_DESCRIPTION_KEY],
          [SUB_TOPIC_TOTAL_COMMENT_COUNT_KEY_FOR_CSV]: subTopicTotalCommentCount,
          [TOP_5_KEYWORDS_KEY_FOR_CSV]: subTopicData[TOP_5_KEYWORDS_KEY].join(','),
          [ANGER_COUNT_KEY_FOR_CSV]: subTopicData[EMOTION_COMMENT_COUNTS_KEY][ANGER],
          [FEAR_COUNT_KEY_FOR_CSV]: subTopicData[EMOTION_COMMENT_COUNTS_KEY][FEAR],
          [JOY_COUNT_KEY_FOR_CSV]: subTopicData[EMOTION_COMMENT_COUNTS_KEY][JOY],
          [SADNESS_COUNT_KEY_FOR_CSV]: subTopicData[EMOTION_COMMENT_COUNTS_KEY][SADNESS],
          [NO_SPECIFIC_EMOTION_KEY_FOR_CSV]: subTopicData[EMOTION_COMMENT_COUNTS_KEY][NO_SPECIFIC_EMOTION],
          [X_KEY_FOR_CSV]: subTopicData[DISTRIBUTION_KEY][X_KEY],
          [Y_KEY_FOR_CSV]: subTopicData[DISTRIBUTION_KEY][Y_KEY]
      };
      parsedData.push(parsedSubTopicData);
    }
    return parsedData;
  }

  parseCsvFileName() {
    const fromDate = this.state.filterDateRange.fromDate;
    const toDate = this.state.filterDateRange.toDate;
    let fileName = "sub_topic_table_" + fromDate + "_" + toDate + ".csv";
    return fileName;
  }

  highlightClickedDataRow(e) {
    if (e.point.options) {
      const clickedSubTopicTitle = e.point.options.subTopicTitle;
      const clickedIndex = this.retrieveSubTopicIndexInChartData(clickedSubTopicTitle);
      this.setState({ selectedDataIndex: clickedIndex }, () => {
        const rows = this.rowRefs.map(ref => ref.current);
        let scrollToPosition = 0;
        for (let i = 0; i < clickedIndex; i++) {
            scrollToPosition += rows[i].offsetHeight;
        }
        this.tableContainerRef.current.scrollTop = scrollToPosition;
      });
    }
  }

  retrieveSubTopicIndexInChartData(clickedSubTopicTitle) {
    const { mapDataFromProps } = this.state;
    const sortedSubTopics = mapDataFromProps[0].data;
    for (let i=0; i<Object.keys(sortedSubTopics).length; i++) {
      if (sortedSubTopics[i][SUB_TOPIC_TITLE_KEY] == clickedSubTopicTitle) {
        return i;
      }
    }
  }

  getI18nText(textKey) {
    return t(MY_I18N_PREFIX + "." + textKey);
  }
  
  componentDidMount() {
    this.reRenderChartOnChange(this.props);
  }

  UNSAFE_componentWillReceiveProps(newProps) {
    const { chartId } = this.props;
    if (
      JSON.stringify(newProps.BubbleChart[chartId]) !== JSON.stringify(this.state.mapDataFromProps) &&
      newProps.selectedTopicId != null
    ) {
      this.setState({ chartData: [] });
      const { title } = this.props;
      const parsedChartData = this.parseDataToRequiredChartDataFormat(newProps.BubbleChart[chartId]);
      const parsedChartDataForCsvDownload = this.parseDataToRequiredCsvDownloadFormat(newProps.BubbleChart[chartId]);
      const parsedCsvDownloadFileName = this.parseCsvFileName();
      const chartData =[{
        data: parsedChartData,
        color: DEFAULT_BUBBLE_COLOUR
    }];
      let { mapOptions } = this.state;
      mapOptions[SERIES_KEY] = chartData;
      mapOptions[TITLE_KEY][TEXT_KEY] = title;
      
      this.setState({
      chartData,
      mapOptions,
      mapDataFromProps: chartData,
      topicId: newProps.selectedTopicId,
      source: newProps.source,
      subTopicTableData: parsedChartDataForCsvDownload,
      fileName: parsedCsvDownloadFileName
      });
      this.rowRefs = chartData[0].data.map(() => React.createRef());
    }

    if (this._shouldReRenderChartBasedOnNewProps(newProps)) {
      this.reRenderChartOnChange(newProps);
    };
    
  }

  handleDownload = () => {
    this.csvLinkEl.current.link.click();
  }

  render() {
    const { mapOptions, mapDataFromProps, chartData, selectedDataIndex } = this.state;
    const topicsData =  chartData[0] ? chartData[0].data : [];
    return (
      <React.Fragment>
      {Object.keys(mapDataFromProps).length > 0 ? (
          <div className='bubble-container'>
            <div className='chart-element'>
              <HighchartsReact
                highcharts={Highcharts}
                options={mapOptions}
                allowChartUpdate={true}
                immutable={false}
                updateArgs={[true, true, true]}
              />
            </div>
            <div className='table-element'>
              <div className='table-container' ref={this.tableContainerRef}>
              <table>
                <thead className="table-header">
                  <tr>
                    <th width={SUB_TOPIC_NAME_TABLE_WIDTH}>{this.getI18nText(MY_I18N_KEYS.sub_topic)}</th>
                    <th width={COMMENT_COUNT_TABLE_WIDTH}>{this.getI18nText(MY_I18N_KEYS.comments_percentage)}</th>
                    <th width={EMOTION_PERCENTAGE_TABLE_WIDTH}>{this.getI18nText(MY_I18N_KEYS.emotions_percentage)}</th>
                    <th>{this.getI18nText(MY_I18N_KEYS.top_5_keywords)}</th>
                    <th width={SUB_TOPIC_DESCRIPTION_TABLE_WIDTH}>
                      {this.getI18nText(MY_I18N_KEYS.sub_topic_description)} 
                      <Button
                        icon="download"
                        label={this.getI18nText(MY_I18N_KEYS.csv)}
                        inputClassName="btn-download"
                        onClick={() => {this.handleDownload()}}
                      />
                      <CSVLink
                        data={this.state.subTopicTableData}
                        filename={this.state.fileName}
                        ref={this.csvLinkEl}  
                      />
                    </th>
                  </tr>
                </thead>
                <tbody>
                {topicsData ? topicsData.map((subTopicData, index) => (
                  <tr className={`${index === selectedDataIndex ? 'active' : ''}`}
                    key={subTopicData[SUB_TOPIC_TITLE_KEY]}
                    ref={this.rowRefs[index]}
                  >
                    <td>{subTopicData[SUB_TOPIC_TITLE_KEY].split('_').join(' ')}</td>
                    <td>{subTopicData[SUB_TOPIC_TOTAL_COMMENT_COUNT_KEY]} ({subTopicData[SUB_TOPIC_COMMENT_PERCENTAGE_KEY]}%)</td>
                    <td>
                      {this.getI18nText(MY_I18N_KEYS.anger)} ({subTopicData[EMOTION_PERCENTAGE_COUNTS_KEY][ANGER]}%)<br/>
                      {this.getI18nText(MY_I18N_KEYS.fear)} ({subTopicData[EMOTION_PERCENTAGE_COUNTS_KEY][FEAR]}%)<br/>
                      {this.getI18nText(MY_I18N_KEYS.joy)} ({subTopicData[EMOTION_PERCENTAGE_COUNTS_KEY][JOY]}%)<br/>
                      {this.getI18nText(MY_I18N_KEYS.sadness)} ({subTopicData[EMOTION_PERCENTAGE_COUNTS_KEY][SADNESS]}%)<br/>
                      {this.getI18nText(MY_I18N_KEYS.no_specific_emotion)} ({subTopicData[EMOTION_PERCENTAGE_COUNTS_KEY][NO_SPECIFIC_EMOTION]}%)</td>
                    <td>
                      {
                        subTopicData[TOP_5_KEYWORDS_KEY].map((keyword, _) => (
                          <span key={keyword}>{keyword}<br/></span>
                        ))
                      }
                    </td>
                    <td>{subTopicData[SUB_TOPIC_DESCRIPTION_KEY]}</td>
                  </tr>
                )) : null}
                </tbody>
              </table>
              </div>
            </div>
          </div>
      ) : (
          <HighchartsReact highcharts={Highcharts} options={mapOptions} />
      )}
      </React.Fragment>
  );
  }
}

function mapStateToProps(state) {
  return {
    BubbleChart: state.charts,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({ ...actions }, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(BubbleChart);


// WEBPACK FOOTER //
// ./src/features/shared-components/chart/BubbleChart.js